import { ChangeDetectionStrategy, Component, ViewChild, computed, inject, input, output, signal, model } from '@angular/core';
import { ProductionBatch, Scan, ScanStatus } from '../../../model';
import { AuthenticationState } from '../../../store/auth/auth.state';
import { BackendService } from '../../../services/backend';
import { ScannerState } from '../../../store/scanner/scanner.state';
import { ProductionBatchState } from '../../../store/batch/batch.state';
import { UserRole } from '../../../model';
import { fallbackImage } from '../index';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { FormsModule } from '@angular/forms';
import { FailureReasonComponent } from '../failure-reason/failure-reason.component';
import { OperatorReasonDialogComponent } from '../operator-reason-dialog/operator-reason-dialog.component';

export interface DlgBadBucketPositionData {
  scan: Scan;
  batch: ProductionBatch | null | undefined;
  isInViewMode: boolean;
}

/**
 * Non-modal floating panel for bad-bucket-position alerts.
 * Mount via BadBucketPanelService.open() — do not place in templates.
 */
@Component({
  selector: 'app-dlg-bad-bucket-position',
  templateUrl: './dlg-bad-bucket-position.component.html',
  styleUrl: './dlg-bad-bucket-position.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NzButtonModule,
    NzRadioModule,
    NzSpinModule,
    NzSpaceModule,
    NzIconModule,
    NzImageModule,
    NzGridModule,
    NzTooltipModule,
    FailureReasonComponent,
    OperatorReasonDialogComponent,
  ]
})
export class DlgBadBucketPositionComponent {
  readonly scan = model.required<Scan>();
  readonly batch = input<ProductionBatch | null | undefined>();
  readonly isInViewMode = input<boolean>(false);
  readonly closed = output<Scan>();

  protected readonly authState = inject(AuthenticationState);
  private readonly backend = inject(BackendService);
  private readonly scannerState = inject(ScannerState);
  protected readonly productionBatchState = inject(ProductionBatchState);
  private readonly modalService = inject(NzModalService);

  readonly fallbackImage = fallbackImage;
  protected readonly busy = signal(false);
  protected readonly selectedStatus = signal<ScanStatus>(ScanStatus.Failed);

  /** Reference to the embedded reason-picker popup (live branch / AC-2). */
  @ViewChild(OperatorReasonDialogComponent)
  private reasonDialog?: OperatorReasonDialogComponent;

  /** Status awaiting Operator reason confirmation (live branch only). */
  private _pendingStatus: ScanStatus | null = null;

  readonly title = computed(() => {
    const s = this.scan();
    const b = this.batch();
    return `Scan number ${s.sequenceInBatch} in batch ${b?.poName ?? '?'} - Bad Position Alert`;
  });

  /**
   * Returns the raw ML error detail string for display purposes (e.g. 'BadBucketPosition').
   * Returns null when no MlError reason is present.
   */
  protected get mlErrorDetail(): string | null {
    const mlReason = this.scan().reasons?.find((r) => r.code === 'MlError');
    return mlReason?.detail ?? null;
  }

  // --- drag support ---

  onHeaderMouseDown(e: MouseEvent): void {
    const panel = (e.currentTarget as HTMLElement).closest('app-dlg-bad-bucket-position2') as HTMLElement;
    if (!panel) return;

    // Switch from right-anchored to left-anchored so left/top are stable during drag
    const rect = panel.getBoundingClientRect();
    panel.style.right = 'auto';
    panel.style.left = rect.left + 'px';
    panel.style.top = rect.top + 'px';

    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    const onMove = (ev: MouseEvent) => {
      panel.style.left = ev.clientX - offsetX + 'px';
      panel.style.top = ev.clientY - offsetY + 'px';
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    e.preventDefault();
  }

  // --- actions ---

  /**
   * AC-2: Intercepts a status change from the radio group.
   * - View mode (C1): always delegates to productionBatchState — no popup (C1 is out of scope).
   * - Live mode (C2), Operator: stores pending status and opens the reason popup.
   * - Live mode (C2), non-Operator: dispatches setManualInspectionResult directly (no popup).
   */
  protected onStatusChange(status: ScanStatus): void {
    console.log('DlgBadBucketPosition2Component.onStatusChange()', { status, isInViewMode: this.isInViewMode() });
    this.selectedStatus.set(status);
    if (this.isInViewMode()) {
      // C1 view branch — out of scope for AC-2; no popup.
      this.productionBatchState.updateStatus(this.scan(), status);
      console.log('DlgBadBucketPosition2Component.onStatusChange() did productionBatchState.updateStatus({status})', status);
    } else if (this.authState.hasExactlyRole(UserRole.Operator)) {
      // C2 live branch + Operator — show reason popup before committing.
      this._pendingStatus = status;
      this.reasonDialog?.open();
    } else {
      // C2 live branch + non-Operator — dispatch immediately, no popup.
      // Fire-and-forget: nothing here needs the result. A second manual-inspection POST after the scanner has left
      // Deciding puts the scanner into Failed; subscribing used to send it twice, fixed by singleRequest() (DEV-314).
      this.scannerState.setManualInspectionResult(status);
    }
  }

  /**
   * AC-2 + AC-3: Operator confirmed a reason in the popup — dispatch with reason key.
   *
   * VERIFY #1 (runlog): setManualInspectionResult returns ScannerState (not Scan).
   * Scan.operatorStatusChangeReason propagates back to the gallery via the next SSE batch push
   * (BackendService.getBatchesUpdated → ProductionBatchState.upsertOne) — confirmed: the
   * backend's scan update after a manual inspection triggers the SSE event that carries
   * the updated batch including the updated scan's operatorStatusChangeReason.
   */
  onReasonSelected(reasonKey: string): void {
    if (!this._pendingStatus) return;
    const status = this._pendingStatus;
    this._pendingStatus = null;
    // Fire-and-forget — see onStatusChange: subscribing here would send the POST twice.
    this.scannerState.setManualInspectionResult(status, reasonKey);
  }

  /** AC-2: Operator cancelled — clear pending, no backend call. Radio reverts via signal. */
  onReasonCancelled(): void {
    if (this._pendingStatus) {
      // Revert the visual radio selection back to whatever was set before.
      const currentState = this.scan().state;
      this.selectedStatus.set(currentState);
    }
    this._pendingStatus = null;
  }

  protected btnClose(): void {
    console.log('DlgBadBucketPosition2Component.btnClose() - closing panel');
    this.closed.emit(this.scan());
  }

  protected btnRetake(): void {
    const scan = this.scan();
    const { id, productionBatchId } = scan;
    console.log('DlgBadBucketPosition2Component.btnRetake() called', { scanId: id, batchId: productionBatchId });
    this.busy.set(true);
    this.backend.retakeAndInspect(productionBatchId, id).subscribe({
      next: (updatedScan) => {
        console.log('DlgBadBucketPosition2Component.btnRetake() success', { updatedScan });
        this.scan.set(updatedScan);
        this.busy.set(false);
        //        this.closed.emit(updatedScan);
      },
      error: (err) => {
        console.error('DlgBadBucketPosition2Component.btnRetake() error', err);
        this.busy.set(false);
        this.closed.emit(scan);
      }
    });
  }

  protected onImageError(): void {
    const url = this.scan().imageUrl;
    console.warn('DlgBadBucketPosition2Component.onImageError() - image failed to load', { imageUrl: url });
    this.modalService.error({
      nzTitle: 'Image Load Error',
      nzContent: `Failed to load scan image: ${url ?? 'unknown URL'}`,
    });
  }
}
